<!-- src/views/PaysView.vue -->
<template>
  <div class="p-6 max-w-7xl mx-auto">
    <h3 class="text-center">CALCULATE PAY INTERFACE</h3>
    
    <!-- Controls -->
    <div class="flex items-center justify-center gap-4 mb-6 flex-wrap">
      <div class="flex items-center gap-2">
        <label class="font-medium">Start Date</label>
        <input type="date" v-model="startDate" class="border rounded px-3 py-1">
      </div>
      <div class="flex items-center gap-2">
        <label class="font-medium">End Date</label>
        <input type="date" v-model="endDate" class="border rounded px-3 py-1">
      </div>
      <div class="flex items-center gap-2">
        <label class="font-medium">Group</label>
        <select v-model="selectedGroupId" class="border rounded px-3 py-1">
          <option :value="null">All Officers</option>
          <option v-for="group in officerGroups" :key="group.id" :value="group.id">{{ group.name }}</option>
        </select>
      </div>
      <button v-if="selectedIds.length <= 0" @click="calculatePays" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isCalculating ? 'Calculating...' : 'Calculate Pays' }}
      </button>
      <button v-if="selectedIds.length <= 0" @click="showPays" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isLoading ? 'Loading...' : 'Display Pays' }}
      </button>
      <button v-if="results && results.length > 0 && selectedIds.length <= 0" @click="downloadExcel" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isLoading ? 'Generating...' : 'Pay Report' }}
      </button>
      <button v-if="results && results.length > 0 && selectedIds.length <= 0" @click="showPaysWithDetail" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isLoading ? 'Loading...' : 'Detailed Report' }}
      </button>
      <!-- Delete Button -->
      <button v-if="results && results.length > 0 && selectedIds.length > 0" @click="deleteSelectedPays" class="bg-primary text-white px-6 py-1 rounded hover:bg-red-700" :disabled="isDeleting">
        {{ isDeleting ? 'Deleting...' : 'Delete Selected (' + selectedIds.length + ')' }}
      </button>
    </div>
    
    <!-- Totals -->
    <div>
      <span v-if="results && results.length > 0" class="p-2 m-2">Total Hours = {{totalHoursForAllPays}}</span>
      <span v-if="results && results.length > 0" class="p-2 m-2">Total Pay = {{paysTotals}}</span>
    </div>


    <!-- Table -->
    <div class="overflow-x-auto">

    <table class="border-collapse border mx-auto">
      <thead>
        <tr class="bg-warning">
          <th class="border p-2 text-left">
            <input type="checkbox" @change="toggleAll" :checked="selectedIds.length === results.length && results.length > 0">
          </th>
          <th class="border p-2 text-left">Officer</th>
          <th class="border p-2 text-right">Mobile Number</th>
          <th class="border p-2 text-left">Start Date</th>
          <th class="border p-2 text-left">End Date</th>
          <th class="border p-2" style="text-align: right;">Hours</th>
          <th class="border p-2" style="text-align: right;">T.Ex.</th>
          <th class="border p-2" style="text-align: right;">Pay</th>
          <th class="border p-2 text-left">BankAccount</th>
          <th class="border p-2 text-left">Detail</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!results || results.length === 0">
          <td colspan="10" class="border p-2 text-center text-gray-500">No records found</td>
        </tr>
        <tr v-for="(row, index) in results" :key="index">
          <td class="border p-2 text-left">
            <input type="checkbox" :value="row.payId" v-model="selectedIds">
          </td>
          <td class="border p-2 text-left">{{ row.officerFullNameWithOutID || '-' }}</td>
          <td class="border p-2 text-right">{{ row.officerMobileNumber || '-' }}</td>
          <td class="border p-2">{{ row.startDate || '-' }}</td>
          <td class="border p-2">{{ row.endDate || '-' }}</td>
          <td class="border p-2 text-right" style="text-align: right;">{{ row.totalHours?.toFixed(2) || '0' }}</td>
          <td class="border p-2" style="text-align: right;">{{ row.travelExpense?.toFixed(2) || '0' }}</td>
          <td class="border p-2" style="text-align: right;">{{ row.roundedPay?.toFixed(2) || '-' }}</td>
          <td class="border p-2 text-left">{{ row.bankAccountDetail || '-' }}</td>
          <td class="border p-2 text-left">
            <span @click="showPayDetail(row.payId)" class="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
              Detail
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    </div>
  </div>

  <!-- MODAL -->
  <div v-if="showPayDetailModal" style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;overflow-y:auto;">
    <div style="background:white;border-radius:8px;max-width:90%;width:1000px;max-height:90vh;overflow-y:auto;margin:20px;">
      <PayDetail 
        :baseUrl="baseUrl" 
        :payId="selectedPayId"
        @close="closePayDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import PayDetail from '@/components/PayDetail.vue'

const props = defineProps({
  baseUrl: { type: String, required: true }
})

const startDate = ref('')
const endDate = ref('')
const isLoading = ref(false)
const isCalculating = ref(false)
const isBusy = ref(false)
const isDeleting = ref(false)
const paysTotals = ref(0.0)
const totalHoursForAllPays = ref(0.0)
const results = ref([])
const showPayDetailModal = ref(false)
const selectedPayId = ref(null)
const selectedIds = ref([])
const companyName = ref(null)
const selectedGroupId = ref(null)
const officerGroups = ref([])

const loadOfficerGroups = () => {
  const cachedData = localStorage.getItem("lookupData")
  if (!cachedData) return
  officerGroups.value = JSON.parse(cachedData).officerGroups || []
}

onMounted(() => {
  loadOfficerGroups()
})

const showPayDetail = (payId) => {
  if (!payId) { alert('Invalid pay ID'); return }
  selectedPayId.value = payId
  showPayDetailModal.value = true
}

//companyName = localStorage.getItem("companyName");

const closePayDetail = () => {
  showPayDetailModal.value = false
  selectedPayId.value = null
}

const toggleAll = (event) => {
  selectedIds.value = event.target.checked ? results.value.map(r => r.payId) : []
}

const deleteSelectedPays = async () => {
  if (!selectedIds.value.length) { alert('No records selected'); return }
  if (!confirm(`Delete ${selectedIds.value.length} selected pay record(s)?`)) return

  isDeleting.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/DeletePays", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify(selectedIds.value)
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json();

    // Remove deleted records from results
    results.value = results.value.filter(r => !selectedIds.value.includes(r.payId))
    selectedIds.value = []
    paysTotals.value = results.value.reduce((sum, item) => sum + (item.roundedPay || 0), 0).toFixed(2);
    totalHoursForAllPays.value = results.pays.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(2);

    alert(data.message || 'Records deleted successfully')
  } catch (e) {
    alert('Failed to delete: ' + e.message)
    console.log('Failed to delete: ' , e.message)
  } finally {
    isDeleting.value = false
    isBusy.value = false
  }
}

const calculatePays = async () => {
  if (!startDate.value || !endDate.value) { alert('Please select both dates'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isCalculating.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/CalculatePays", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value, officerGroupId: selectedGroupId.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    results.value = data.pays.map(row => ({
      ...row,
      startDate: new Date(row.startDate).toLocaleDateString('en-GB'),
      endDate: new Date(row.endDate).toLocaleDateString('en-GB')
    }))
    paysTotals.value = data.pays.reduce((sum, item) => sum + (item.roundedPay || 0), 0).toFixed(2);
    totalHoursForAllPays.value = data.pays.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(2);

    alert(data.message)
  } catch (e) {
    alert('Failed to calculate pays: ' + e.message)
  } finally {
    isCalculating.value = false
    isBusy.value = false
  }
}

const showPays = async () => {
  if (!startDate.value || !endDate.value) { alert('Please select both dates'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isLoading.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/ShowPays", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value, officerGroupId: selectedGroupId.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    results.value = data.pays.map(row => ({
      ...row,
      startDate: new Date(row.startDate).toLocaleDateString('en-GB'),
      endDate: new Date(row.endDate).toLocaleDateString('en-GB')
    }))

    paysTotals.value = data.pays.reduce((sum, item) => sum + (item.roundedPay || 0), 0).toFixed(2);
    totalHoursForAllPays.value = data.pays.reduce((sum, item) => sum + (item.totalHours || 0), 0).toFixed(2);

    alert(data.message)
  } catch (e) {
    alert('Failed to load pays: ' + e.message)
  } finally {
    isLoading.value = false
    isBusy.value = false
  }
}

const showPaysWithDetail = async () => {
  if (!startDate.value || !endDate.value) { alert('Please select both dates'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isLoading.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/ShowPaysWithDetails", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value, officerGroupId: selectedGroupId.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()

    // Get company name from localStorage
    const companyName = localStorage.getItem("companyName") || 'Company Name';

    // Start building HTML table
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" 
            xmlns:x="urn:schemas-microsoft-com:office:excel" 
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Detailed Pay Report</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          table { 
            border-collapse: collapse; 
            font-family: Calibri, Arial, sans-serif; 
            font-size: 18px;
            width: 100%;
          }
          th, td { 
            border: 1px solid #000000; 
            padding: 4px 6px; 
          }
          th { 
            background-color: #0000ff; 
            color: #ffffff; 
            font-weight: bold;
            text-align: center;
          }
          .center { text-align: center; }
          .left { text-align: left; }
          .right { text-align: right; }
          .number { 
            text-align: center;
            font-variant-numeric: tabular-nums;
          }
          .officer-header {
            font-size: 18px;
            font-weight: bold;
            background-color: #e0a10e;
            padding: 6px 8px;
          }
          .total-row {
            font-weight: bold;
            background-color: #D9E1F2;
            text-align: center;
          }
          .grand-total {
            font-weight: bold;
            font-size: 20px;
            background-color: #D9E1F2;
            text-align: center;
          }
          .section-spacer {
            height: 10px;
            background-color: #ffffff;
            border: none;
          }
          .table-heading {
            font-size: 18px;
            background-color: #0000ff;
            color: #ffffff;
            colspan:10
          }
        </style>
      </head>
      <body>
        <table>
    `;

    // Main Title
    html += `<tr>`;
    html += `<td colspan="10" style="font-size: 24px; font-weight: bold; text-align: center; background-color: #e8f5e9; padding: 10px;">
      Detailed Pay Report For ${localStorage.getItem("companyName")} (${startDate.value} to ${endDate.value})
    </td>`;
    html += `</tr>`;

    let totalHoursForAllPays = 0;
    let totalAmountForAllPays = 0;

    // Check if data.pays exists and is an array
    if (!data.pays || !Array.isArray(data.pays)) {
      throw new Error('No pay data available');
    }

    data.pays.forEach((pay, payIndex) => {
      // Safety checks for pay object
      if (!pay) return;
      
      // Add spacer between officers (except for the first one)
      if (payIndex > 0) {
        html += `<tr><td colspan="10" style="height: 10px; border: none;"></td></tr>`;
      }

      // Officer header row - spans 10 columns
      const officerName = (pay.officerName || 'Unknown Officer').toUpperCase();
      const totalPay = pay.payAmount ? `£${Number(pay.payAmount).toFixed(2)}`.toUpperCase() : '£0.00';
      const totalHours = pay.totalHours ? `(${Number(pay.totalHours).toFixed(2)}hrs)`.toUpperCase() : '(0hrs)';
      
      html += `<tr>`;
      html += `<td colspan="10" class="officer-header">
        <!-- ${officerName} - ${totalHours} - ${totalPay} -->
        ${officerName}
      </td>`;
      html += `</tr>`;

      // Column headers
      const headers = ['S.N.', 'SITE', 'Date', 'Time', 'Adj', 'Bonus Hrs', 'TA/DA', 'Net.Duty', 'Rate', 'Wages'];
      html += `<tr>`;
      headers.forEach(header => {
        html += `<th class="table-heading">${header}</th>`;
      });
      html += `</tr>`;

      // Check if pay.jobs exists and is an array
      const jobs = Array.isArray(pay.jobs) ? pay.jobs : [];
      
      // Data rows
      jobs.forEach((job, idx) => {
        // Safety checks for job values
        const dutyHours = Number(job.dutyHours) || 0;
        const adjustmentHours = Number(job.adjustmentHours) || 0;
        const bonusHours = Number(job.bonusHours) || 0;
        const payRate = Number(job.payRate) || 0;
        const travelExpense = Number(job.travelExpense) || 0;
        
        const netHours = dutyHours + adjustmentHours + bonusHours;
        const wages = netHours * payRate + travelExpense;
        
        totalHoursForAllPays += netHours;
        totalAmountForAllPays += wages;

        // Determine if this is a weekend (Saturday/Sunday) - optional coloring
        let isWeekend = false;
        try {
          const jobDate = new Date(job.jobDate);
          const dayOfWeek = jobDate.getDay();
          isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        } catch (e) {
          // If date parsing fails, ignore
        }
        // Format time
        let timeStr = '-';
        if (job.startTime && job.endTime) {
          timeStr = `${String(job.startTime).slice(0,5)}-${String(job.endTime).slice(0,5)}`;
        } else if (job.startTime) {
          timeStr = `${String(job.startTime).slice(0,5)}-`;
        } else if (job.endTime) {
          timeStr = `-${String(job.endTime).slice(0,5)}`;
        }

        // Format date
        let dateStr = '-';
        try {
          if (job.jobDate) {
            dateStr = new Date(job.jobDate).toLocaleDateString('en-GB');
          }
        } catch (e) {
          dateStr = job.jobDate || '-';
        }

        html += `<tr>`;
        html += `<td class="number">${idx + 1}</td>`;
        html += `<td class="left">${job.siteInfo || job.siteName || ''}</td>`;
        html += `<td class="center">${dateStr}</td>`;
        html += `<td class="center">${timeStr}</td>`;
        html += `<td class="number">${adjustmentHours}</td>`;
        html += `<td class="number">${bonusHours}</td>`;
        html += `<td class="number">${travelExpense.toFixed(2)}</td>`;
        html += `<td class="number">${netHours.toFixed(2)}</td>`;
        html += `<td class="number">£${payRate.toFixed(2)}</td>`;
        html += `<td class="number">£${wages.toFixed(2)}</td>`;
        html += `</tr>`;
      });

      // Total row for this officer
      const officerTotalPay = Number(pay.payAmount) || 0;
      const officerRate = jobs.length > 0 ? Number(jobs[0]?.payRate) || 0 : 0;
      
      html += `<tr>`;
      html += `<td class="total-row" colspan="8" style="text-align: right; font-weight: bold;">TOTAL</td>`;
      html += `<td class="total-row">£${officerRate.toFixed(2)}</td>`;
      html += `<td class="total-row">£${officerTotalPay.toFixed(2)}</td>`;
      html += `</tr>`;
    });

    // Grand Total row
    html += `<tr><td colspan="10" style="height: 10px; border: none;"></td></tr>`;
    html += `<tr class="grand-total total-row">`;
    html += `<td colspan="8" style="text-align: right; font-weight: bold; font-size: 14px;">GRAND TOTAL</td>`;
    html += `<td class="total-row" style="font-weight: bold; font-size: 14px;">${totalHoursForAllPays.toFixed(2)} hrs</td>`;
    html += `<td class="total-row" style="font-weight: bold; font-size: 14px;">£${totalAmountForAllPays.toFixed(2)}</td>`;
    html += `</tr>`;

    // Summary row
    html += `<tr>`;
    html += `<td colspan="10" style="text-align: center; font-size: 12px; padding-top: 10px; border: none;">
      Generated on: ${new Date().toLocaleString()}
    </td>`;
    html += `</tr>`;

    html += `</table></body></html>`;

    // Create and download as .xls
    const blob = new Blob(['\uFEFF' + html], {
      type: 'application/vnd.ms-excel;charset=utf-8'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Detailed_Pay_Report_${startDate.value}_to_${endDate.value}.xls`;
    link.click();
    URL.revokeObjectURL(link.href);

    alert(data.message || 'Report downloaded successfully!')
  } catch (e) {
    console.error('Error:', e);
    alert('Failed to load pays: ' + (e.message || 'Unknown error'))
  } finally {
    isLoading.value = false
    isBusy.value = false
  }
}

const downloadExcel = () => {
  if (results.value.length === 0) { 
    alert('No data to export.'); 
    return 
  }

  // Get company name from localStorage
  const companyName = localStorage.getItem("companyName") || 'Company Name';

  // Column headers
  const headers = [
    'Officer',
    'Mobile Number',
    'Start Date',
    'End Date',
    'Total Hours',
    'Travel Expense',
    'Rate',
    'Pay',
    'Bank Account'
  ]

  // Start building HTML table
  let html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" 
          xmlns:x="urn:schemas-microsoft-com:office:excel" 
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Pay Report</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        table { 
          border-collapse: collapse; 
          font-family: Calibri, Arial, sans-serif; 
          font-size: 18px;
          width: 100%;
        }
        th, td { 
          border: 1px solid #446ced; 
          padding: 5px 8px; 
        }
        th { 
          background-color: #0000ff; 
          color: #ffffff; 
          font-weight: bold;
          text-align: center;
        }
        .center { text-align: center; }
        .left { text-align: left; }
        .total { 
          font-weight: bold;
          background-color: #D9E1F2;
        }
        .number { 
          text-align: center;
          font-variant-numeric: tabular-nums;
        }
        .top-heading {
          font-size: 24px;
          font-weight: bold;
          text-align: center;
          background-color: #e8f5e9;
          padding: 15px 8px;
        }
        .table-header {
          font-size: 24px;
          font-weight: bold;
          text-align: center;
          background-color: #0000ff;
          padding: 15px 8px;
          color: #ffffff
        }
      </style>
    </head>
    <body>
      <table>
  `

  // Add top heading row - spans all 9 columns
  html += `<tr>`
  html += `<td colspan="9" class="top-heading">Pay Report For ${companyName} Between ${startDate.value} and ${endDate.value}</td>`
  html += `</tr>`

  // Add headers
  html += `<thead><tr>`
  headers.forEach(header => {
    html += `<th class="table-header">${header}</th>`
  })
  html += `</tr></thead><tbody>`

  // Build rows - using the same logic as your original code
  results.value.forEach((row, idx) => {
    const rowIndex = idx + 3; // header is row 2
    
    html += `<tr>`
    html += `<td class="left">${row.officerFullNameWithOutID || '-'}</td>`
    html += `<td class="center">${row.officerMobileNumber || '-'}</td>`
    html += `<td class="center">${row.startDate || '-'}</td>`
    html += `<td class="center">${row.endDate || '-'}</td>`
    html += `<td class="number">${Number(row.totalHours || 0).toFixed(2)}</td>`
    html += `<td class="number">${Number(row.travelExpense || 0).toFixed(2)}</td>`
    html += `<td class="number">${Number(row.rate || 0).toFixed(2)}</td>`
    // Pay column with formula
    html += `<td class="number">=E${rowIndex}*G${rowIndex}+F${rowIndex}</td>`
    html += `<td class="center">${row.bankAccountDetail || '-'}</td>`
    html += `</tr>`
  })

  // Totals row
  const totalRows = results.value.length + 1
  html += `<tr class="total">`
  html += `<td>TOTAL</td>`
  html += `<td></td>`
  html += `<td></td>`
  html += `<td></td>`
  html += `<td class="number">=SUM(E2:E${totalRows})</td>`
  html += `<td class="number">=SUM(F2:F${totalRows})</td>`
  html += `<td></td>`
  html += `<td class="number">=SUM(H2:H${totalRows})</td>`
  html += `<td></td>`
  html += `</tr>`

  html += `</tbody></table></body></html>`

  // Create and download as .xls (Excel will open HTML format)
  const blob = new Blob([html], { 
    type: 'application/vnd.ms-excel;charset=utf-8' 
  })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `Pay_Report_${startDate.value}_to_${endDate.value}.xls`
  link.click()
  URL.revokeObjectURL(link.href)
}

</script>